import type { ApiConfig } from '@de/types/config';
import type { InterpolatedRequest } from '../data-sources/fetchDataSource';
import { normalizeRankingsResponse } from '../mappers/normalizeRankingsResponse';
import { isRankingsListPath } from './rankingsResultsSourceIdentity';

/**
 * Build a post-fetch response transform that pre-derives the composite
 * `RankingCard` arrays (`marketing.stats` / `.details` / `.badges`) from the
 * organizations-service rankings list response
 * (`POST /v1/rankings/{listKey}`). Delegates the per-item reshape to
 * {@link normalizeRankingsResponse}; this factory only owns the
 * origin/path/method gate (mirrors {@link buildOfferingsMatchResponseCoercer}).
 *
 * Returns `undefined` when no organizations origin is configured, so callers
 * can compose the result without a null branch. The returned transform is
 * pure: anything that isn't a POST to a `/v1/rankings/{listKey}` route on the
 * configured organizations origin returns the response unchanged.
 *
 * This is an interim seam; the durable fix is service-/BFF-side derivation of
 * these arrays — retire this when that lands.
 */
export function buildRankingsResponseCoercer(
  apiConfig: ApiConfig,
): ((res: unknown, req: InterpolatedRequest) => unknown) | undefined {
  const organizationOrigins = new Set(
    [apiConfig.organizationServiceBaseUrl, apiConfig.organizationApiUrl]
      .map(extractOrigin)
      .filter((origin): origin is string => origin !== null),
  );
  if (organizationOrigins.size === 0) return undefined;

  return (res, req) => {
    if (req.method !== 'POST') return res;
    if (!isRankingsListPath(pathnameOf(req.endpoint))) return res;
    // Absolute endpoints (production shape after declaration resolution) must
    // target an organizations origin; path-only endpoints (tests / standalone
    // hosts) carry no origin and are gated on the path alone.
    const reqOrigin = extractOrigin(req.endpoint);
    if (reqOrigin && !organizationOrigins.has(reqOrigin)) return res;
    return normalizeRankingsResponse(res);
  };
}

function pathnameOf(endpoint: string): string {
  if (endpoint.startsWith('/')) {
    return endpoint.split(/[?#]/)[0] ?? '';
  }
  try {
    return new URL(endpoint).pathname;
  } catch {
    return '';
  }
}

function extractOrigin(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.origin.toLowerCase();
  } catch {
    return null;
  }
}
